/**
 * @file
 * Node pseudo filter plugin for handling node selection filters.
 *
 * Extends the base PseudoFilterHandler to support both single-selection
 * (radio buttons) and multiple-selection (checkboxes) for node filters.
 *
 * @param {object} Drupal - The Drupal object.
 * @param {Function} once - The once function.
 */

(function (Drupal, once) {
  'use strict';

  /**
   * Node Pseudo Filter Handler.
   *
   * Handles both single-selection (radio) and multiple-selection (checkbox)
   * node filters.
   */
  class NodePseudoFilterHandler extends window.PseudoFilterHandler {

    /**
     * Constructor.
     *
     * @param {HTMLElement} wrapper - The wrapper element.
     * @param {Object} settings - The settings object.
     */
    constructor(wrapper, settings) {
      super(wrapper, settings);
      this.isMultiple = wrapper.dataset.multiple === 'true';
      this.bundles = wrapper.dataset.bundles ? wrapper.dataset.bundles.split(',') : [];
    }

    /**
     * Set up input management (checkboxes or radio buttons).
     */
    setupCheckboxes() {
      if (this.isMultiple) {
        // Multiple selection - use checkboxes
        this.allCheckboxes = this.wrapper.querySelectorAll('input[type="checkbox"]');
      }
      else {
        // Single selection - use radio buttons
        this.allCheckboxes = this.wrapper.querySelectorAll('input[type="radio"]');
      }

      this.syncWithRealFilter();
      this.updateSummaryDisplay();
    }

    /**
     * Attach event listeners.
     */
    attachEventListeners() {
      // Remove any existing listeners first
      this.removeEventListeners();

      // Bind methods to maintain context
      if (!this.boundHandleInputChange) {
        this.boundHandleInputChange = this.handleInputChange.bind(this);
      }
      if (!this.boundHandleFormSubmit) {
        this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      }
      if (!this.boundHandleOutsideClick) {
        this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
      }

      // Listen to input changes (checkbox or radio)
      this.allCheckboxes.forEach(input => {
        input.addEventListener('change', this.boundHandleInputChange);
      });

      // Add keyboard navigation for single-select radio groups.
      if (!this.isMultiple) {
        if (!this.boundHandleRadioKeydown) {
          this.boundHandleRadioKeydown = this.handleRadioKeydown.bind(this);
        }
        this.wrapper.addEventListener('keydown', this.boundHandleRadioKeydown);
      }

      // Listen to form submissions
      if (this.exposedForm) {
        if (this.isAjaxView) {
          jQuery(this.exposedForm).on('submit', this.boundHandleFormSubmit);
        }
        else {
          this.exposedForm.addEventListener('submit', this.boundHandleFormSubmit);
        }
      }

      // Listen to details element open/close
      const detailsElement = this.wrapper.querySelector('details.pseudo-filter-details');
      if (detailsElement) {
        const summaryEl = detailsElement.querySelector('summary');

        // Update aria-expanded preemptively on click so screen readers
        // (e.g. NVDA+Chrome) receive the new state before the toggle fires.
        summaryEl.addEventListener('click', () => {
          summaryEl.setAttribute('aria-expanded', detailsElement.open ? 'false' : 'true');
        });

        detailsElement.addEventListener('toggle', () => {
          summaryEl.setAttribute('aria-expanded', detailsElement.open ? 'true' : 'false');
          if (detailsElement.open) {
            setTimeout(() => {
              document.addEventListener('click', this.boundHandleOutsideClick);
            }, 0);
          }
          else {
            document.removeEventListener('click', this.boundHandleOutsideClick);
            if (this.hoverCloseTimer) {
              clearTimeout(this.hoverCloseTimer);
              this.hoverCloseTimer = null;
            }
          }
        });

        // Add hover event listeners
        this.wrapper.addEventListener('mouseenter', () => {
          if (this.hoverCloseTimer) {
            clearTimeout(this.hoverCloseTimer);
            this.hoverCloseTimer = null;
          }
        });

        this.wrapper.addEventListener('mouseleave', () => {
          if (detailsElement.open) {
            this.hoverCloseTimer = setTimeout(() => {
              detailsElement.open = false;
              document.removeEventListener('click', this.boundHandleOutsideClick);
            }, 1000);
          }
        });
      }
    }

    /**
     * Remove event listeners.
     */
    removeEventListeners() {
      if (this.allCheckboxes && this.boundHandleInputChange) {
        this.allCheckboxes.forEach(input => {
          input.removeEventListener('change', this.boundHandleInputChange);
        });
      }

      if (this.exposedForm && this.boundHandleFormSubmit) {
        if (this.isAjaxView) {
          jQuery(this.exposedForm).off('submit', this.boundHandleFormSubmit);
        }
        else {
          this.exposedForm.removeEventListener('submit', this.boundHandleFormSubmit);
        }
      }

      if (this.boundHandleOutsideClick) {
        document.removeEventListener('click', this.boundHandleOutsideClick);
      }

      if (this.boundHandleRadioKeydown) {
        this.wrapper.removeEventListener('keydown', this.boundHandleRadioKeydown);
      }
    }

    /**
     * Handle input change (checkbox or radio button).
     *
     * @param {Event} event - The change event.
     */
    handleInputChange(event) {
      const input = event.target;
      const nodeId = input.value;

      // Update the real filter
      if (this.isMultiple) {
        // Multiple selection - get all checked values
        const selectedNodes = this.getSelectedNodes();
        this.updateRealFilter(selectedNodes);
      }
      else {
        // Single selection - just the selected value
        this.updateRealFilter([nodeId]);

        // For single selection, close dropdown after selection
        const detailsElement = this.wrapper.querySelector('details.pseudo-filter-details');
        if (detailsElement) {
          setTimeout(() => {
            detailsElement.open = false;
          }, 200); // Small delay for better UX
        }
      }

      // Update summary display
      this.updateSummaryDisplay();

      // Update clear link visibility
      if (typeof window.tnViewsUpdateClearLinkVisibility === 'function') {
        window.tnViewsUpdateClearLinkVisibility();
      }

      // Auto-submit for AJAX views if configured. Skip while a clear is running;
      // the clear handler performs a single authoritative submit and per-input
      // submissions here would race against it.
      if (this.isAjaxView && this.exposedForm && window.tnViewsPseudoFilterAutoSubmit && !window.tnViewsPseudoFiltersClearing) {
        this.debouncedSubmit();
      }
    }

    /**
     * Get selected node IDs.
     *
     * @return {Array} Array of selected node IDs.
     */
    getSelectedNodes() {
      const selected = [];
      this.allCheckboxes.forEach(input => {
        if (input.checked) {
          selected.push(input.value);
        }
      });
      return selected;
    }

    /**
     * Update the real hidden filter with selected values.
     *
     * @param {Array} selectedNodes - Array of selected node IDs.
     */
    updateRealFilter(selectedNodes) {
      if (!this.realFilter) {
        return;
      }

      if (this.realFilter.tagName === 'SELECT') {
        // Update select element
        Array.from(this.realFilter.options).forEach(option => {
          option.selected = selectedNodes.includes(option.value);
        });

        // Trigger change event
        const changeEvent = new Event('change', {bubbles: true});
        this.realFilter.dispatchEvent(changeEvent);
      }
      else {
        // Update hidden inputs or other elements
        // Clear existing values
        const existingInputs = this.realFilter.querySelectorAll('input[type="checkbox"], input[type="radio"]');
        existingInputs.forEach(input => {
          input.checked = selectedNodes.includes(input.value);
        });
      }
    }

    /**
     * Sync pseudo filter state with real filter.
     */
    syncWithRealFilter() {
      if (!this.realFilter) {
        return;
      }

      let selectedValues = [];

      if (this.realFilter.tagName === 'SELECT') {
        // Get selected values from select element
        selectedValues = Array.from(this.realFilter.selectedOptions).map(option => option.value);
      }
      else {
        // Get selected values from hidden inputs
        const checkedInputs = this.realFilter.querySelectorAll('input:checked');
        selectedValues = Array.from(checkedInputs).map(input => input.value);
      }

      // Update pseudo filter inputs
      this.allCheckboxes.forEach(input => {
        input.checked = selectedValues.includes(input.value);
      });
    }

    /**
     * Update summary display text.
     */
    updateSummaryDisplay() {
      const summaryElement = this.wrapper.querySelector('details.pseudo-filter-details summary .summary-placeholder');
      if (!summaryElement) {
        return;
      }

      const selectedInputs = Array.from(this.allCheckboxes).filter(input => input.checked);
      const selectedCount = selectedInputs.length;

      if (selectedCount === 0) {
        summaryElement.textContent = this.isMultiple ? 'Select items' : 'Select one item';
      }
      else if (selectedCount === 1) {
        // Show the selected node title
        const label = selectedInputs[0].nextElementSibling;
        if (label && label.tagName === 'LABEL') {
          summaryElement.textContent = label.textContent.trim();
        }
        else {
          summaryElement.textContent = '1 selected';
        }
      }
      else {
        summaryElement.textContent = `${selectedCount} selected`;
      }

      const summary = summaryElement.closest('summary');
      const filterLabelEl = this.wrapper.querySelector('.pseudo-filter-label');
      if (summary && filterLabelEl) {
        summary.setAttribute('aria-label', `${filterLabelEl.textContent.trim()}: ${summaryElement.textContent}`);
      }
    }

    /**
     * Handle form submit.
     *
     * @param {Event} event - The submit event.
     */
    handleFormSubmit(event) {
      // Ensure real filter is updated before form submission
      const selectedNodes = this.getSelectedNodes();
      this.updateRealFilter(selectedNodes);
    }

    /**
     * Handle keydown events within the radio group.
     *
     * Arrow keys move focus without selecting. Enter confirms the
     * focused option.
     *
     * @param {KeyboardEvent} event - The keydown event.
     */
    handleRadioKeydown(event) {
      const key = event.key;
      const radios = Array.from(this.allCheckboxes);

      switch (key) {
        case 'ArrowDown':
        case 'ArrowRight': {
          event.preventDefault();
          const currentIndex = radios.indexOf(document.activeElement);
          const nextIndex = (currentIndex + 1) % radios.length;
          radios[nextIndex].focus();
          this.scrollOptionIntoView(radios[nextIndex]);
          break;
        }

        case 'ArrowUp':
        case 'ArrowLeft': {
          event.preventDefault();
          const currentIndex = radios.indexOf(document.activeElement);
          const prevIndex = (currentIndex - 1 + radios.length) % radios.length;
          radios[prevIndex].focus();
          this.scrollOptionIntoView(radios[prevIndex]);
          break;
        }

        case 'Enter': {
          const focused = document.activeElement;
          if (focused && focused.type === 'radio' && radios.includes(focused)) {
            event.preventDefault();
            focused.checked = true;
            const changeEvent = new Event('change', {bubbles: true});
            focused.dispatchEvent(changeEvent);
          }
          break;
        }
      }
    }

    /**
     * Scroll the option into view within the scrollable dropdown container.
     *
     * @param {HTMLElement} radio - The radio input element.
     */
    scrollOptionIntoView(radio) {
      const option = radio.closest('.pseudo-filter-option');
      if (option) {
        option.scrollIntoView({block: 'nearest'});
      }
    }

    /**
     * Debounced submit for auto-submit functionality.
     */
    debouncedSubmit() {
      if (this.submitTimeout) {
        clearTimeout(this.submitTimeout);
      }

      this.submitTimeout = setTimeout(() => {
        // Re-find the form if it's not connected to ensure we have the latest reference
        if (!this.exposedForm || !this.exposedForm.isConnected) {
          // Clear the reference first to force findExposedForm to search again
          this.exposedForm = null;
          this.findExposedForm();
        }

        if (this.exposedForm && this.exposedForm.isConnected) {
          const submitButton = this.exposedForm.querySelector('input[type="submit"], button[type="submit"]');
          if (submitButton) {
            submitButton.click();
          }
        }
      }, this.debounceDelay);
    }

    /**
     * Reconnect handler after AJAX update.
     */
    reconnectAfterAjax() {
      // Clear any pending submit timeout
      if (this.submitTimeout) {
        clearTimeout(this.submitTimeout);
        this.submitTimeout = null;
      }

      // Remove existing event listeners first to prevent duplicates
      this.removeEventListeners();

      // Re-check if this is an AJAX view
      const viewContainer = this.wrapper.closest('.view') ||
                          document.querySelector('.view[class*="js-view-dom-id-"]');
      this.isAjaxView = viewContainer &&
                       Array.from(viewContainer.classList).some(cls => cls.startsWith('js-view-dom-id-'));

      // Re-initialize everything with fresh references
      this.findRealFilter();
      this.findExposedForm();

      // Re-query the inputs from the wrapper (they may have been replaced)
      if (this.isMultiple) {
        this.allCheckboxes = this.wrapper.querySelectorAll('input[type="checkbox"]');
      }
      else {
        this.allCheckboxes = this.wrapper.querySelectorAll('input[type="radio"]');
      }

      // Sync state and update display
      this.syncWithRealFilter();
      this.updateSummaryDisplay();

      // Re-attach event listeners to the new inputs
      this.attachEventListeners();
    }
  }

  // Export to global scope for use in plugin registry
  window.NodePseudoFilterHandler = NodePseudoFilterHandler;

})(Drupal, once);
